import { Address, parseAddress } from '../address';
import {
  CorporateGroup,
  DjangoCorporateGroup,
  parseCorporateGroup,
} from '../corporate-group/corporate-group.model';
import {
  DjangoEmployerCreditPolicy,
  EmployerCreditPolicy,
  parseEmployerCreditPolicy,
} from '../credit-policy';
import {
  DjangoEmployerPayrollSettings,
  EmployerPayrollSettings,
  parseEmployerPayrollSettings,
} from '../employer-payroll-settings/employer-payroll-settings.model';
import {
  DjangoEmployerSettings,
  EmployerSettings,
  parseEmployerSettings,
} from '../employer-settings/employer-settings.model';
import { DjangoTimeStamped, TimeStamped } from './../django';
import { applyTimezone, getDateOnly, parseDate } from './../helpers/date';

export class Employer implements TimeStamped {
  id: string;
  name: string;
  nickname: string;
  identity: string;
  active: boolean;
  employeesCount: string;
  warrantExpiresAt: Date;
  contactName: string;
  contactPhoneNumber: string;
  softwareHouse: string;
  address: Address;
  corporateGroup: CorporateGroup;
  settings: EmployerSettings;
  payrollSettings: EmployerPayrollSettings;
  savingsLaunchDate: Date;
  advisorLaunchDate: Date;
  payrollLoanLaunchDate: Date;
  paycheckAdvanceLaunchDate: Date;
  newestPendingChargingFileDate: Date;
  estimatedEmployees: number;
  isHeadquarter: boolean;
  creditPolicies: EmployerCreditPolicy[];
  // DJANGO TIMESTAMPED
  created: Date;
  modified: Date;
  payrollCutoffDay: number;
  isInvoicesAndFilesEnabled: boolean;

  constructor(data: string | Employer | DjangoEmployer = {}) {
    if (data instanceof Employer) {
      bindEmployerData(this, data);
    } else if (typeof data === 'string') {
      bindDjangoEmployerData(this, { id: data });
    } else {
      bindDjangoEmployerData(this, data);
    }
  }

  get djangoModel() {
    return getDjangoModel(this);
  }
}

export function parseEmployer(data?: string | Employer | DjangoEmployer) {
  if (data) {
    return new Employer(data);
  } else {
    return undefined;
  }
}

function bindEmployerData(employer: Employer, data: Employer) {
  employer.id = data.id;
  employer.name = data.name;
  employer.nickname = data.nickname;
  employer.identity = data.identity;
  employer.active = data.active;
  employer.employeesCount = data.employeesCount;
  employer.warrantExpiresAt = data.warrantExpiresAt;
  employer.contactName = data.contactName;
  employer.contactPhoneNumber = data.contactPhoneNumber;
  employer.softwareHouse = data.softwareHouse;
  employer.corporateGroup = data.corporateGroup;
  employer.address = data.address;
  employer.settings = data.settings;
  employer.payrollSettings = data.payrollSettings;
  employer.payrollCutoffDay = data.payrollCutoffDay;
  employer.savingsLaunchDate = data.savingsLaunchDate;
  employer.advisorLaunchDate = data.advisorLaunchDate;
  employer.payrollLoanLaunchDate = data.payrollLoanLaunchDate;
  employer.paycheckAdvanceLaunchDate = data.paycheckAdvanceLaunchDate;
  employer.isInvoicesAndFilesEnabled = data.isInvoicesAndFilesEnabled;
  employer.newestPendingChargingFileDate = data.newestPendingChargingFileDate;
  employer.estimatedEmployees = data.estimatedEmployees;
  employer.isHeadquarter = data.isHeadquarter;
  employer.creditPolicies = data.creditPolicies;
  // DJANGO TIMESTAMPED
  employer.created = data.created;
  employer.modified = data.modified;
}

//////////////////////
/// DJANGO SUPPORT ///
//////////////////////
export interface DjangoEmployer extends DjangoTimeStamped {
  id?: string;
  name?: string;
  nickname?: string;
  identity?: string;
  active?: boolean;
  employees_count?: string;
  warrant_expires_at?: string;
  contact_name?: string;
  contact_phone_number?: string;
  software_house?: string;
  address?: string | Address;
  corporate_group?: string | DjangoCorporateGroup;
  settings?: string | DjangoEmployerSettings;
  payroll_settings?: string | DjangoEmployerPayrollSettings;
  payroll_cutoff_day?: number;
  savings_launch_date?: Date;
  advisor_launch_date?: Date;
  payroll_loan_launch_date?: Date;
  paycheck_advance_launch_date?: Date;
  is_invoices_and_files_enabled?: boolean;
  newest_pending_charging_file_date?: Date;
  estimated_employees?: number;
  is_headquarter?: boolean;
  credit_policies?: DjangoEmployerCreditPolicy[];
  // DJANGO TIMESTAMPED
  created?: string;
  modified?: string;
}

function bindDjangoEmployerData(employer: Employer, data: DjangoEmployer) {
  employer.id = data.id;
  employer.name = data.name;
  employer.nickname = data.nickname;
  employer.identity = data.identity;
  employer.active = data.active;
  employer.employeesCount = data.employees_count;
  employer.warrantExpiresAt = applyTimezone(parseDate(data.warrant_expires_at), 0);
  employer.contactName = data.contact_name;
  employer.contactPhoneNumber = data.contact_phone_number;
  employer.softwareHouse = data.software_house;
  employer.corporateGroup = parseCorporateGroup(data.corporate_group);
  employer.address = parseAddress(data.address);
  employer.settings = parseEmployerSettings(data.settings);
  employer.payrollSettings = parseEmployerPayrollSettings(data.payroll_settings);
  employer.payrollCutoffDay = data.payroll_cutoff_day;
  employer.isInvoicesAndFilesEnabled = data.is_invoices_and_files_enabled;
  employer.savingsLaunchDate = data.savings_launch_date;
  employer.advisorLaunchDate = data.advisor_launch_date;
  employer.payrollLoanLaunchDate = data.payroll_loan_launch_date;
  employer.paycheckAdvanceLaunchDate = data.paycheck_advance_launch_date;
  employer.newestPendingChargingFileDate = data.newest_pending_charging_file_date;
  employer.estimatedEmployees = data.estimated_employees;
  employer.isHeadquarter = data.is_headquarter;
  employer.creditPolicies = [];
  if (data?.credit_policies?.length > 0) {
    employer.creditPolicies = data.credit_policies.map((creditPolicy) =>
      parseEmployerCreditPolicy(creditPolicy),
    );
  }

  // DJANGO TIMESTAMPED
  employer.created = parseDate(data.created);
  employer.modified = parseDate(data.modified);
}

function getDjangoModel(employer: Employer) {
  return {
    id: employer.id,
    name: employer.name,
    identity: employer.identity,
    active: employer.active,
    employees_count: employer.employeesCount,
    warrant_expires_at: getDateOnly(employer.warrantExpiresAt),
    contact_name: employer.contactName,
    contact_phone_number: employer.contactPhoneNumber,
    software_house: employer.softwareHouse,
    address: employer.address,
    corporate_group: employer.corporateGroup,
    settings: employer.settings,
    payroll_settings: employer.payrollSettings,
    payroll_cutoff_day: employer.payrollCutoffDay,
    savings_launch_date: employer.savingsLaunchDate,
    advisor_launch_date: employer.advisorLaunchDate,
    payroll_loan_launch_date: employer.payrollLoanLaunchDate,
    paycheck_advance_launch_date: employer.paycheckAdvanceLaunchDate,
    isInvoicesAndFilesEnabled: employer.isInvoicesAndFilesEnabled,
    newest_pending_charging_file_date: employer.newestPendingChargingFileDate,
    estimated_employees: employer.estimatedEmployees,
    is_headquarter: employer.isHeadquarter,
    credit_policies: employer.creditPolicies,
  };
}
